import * as React from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import type { ClipThumbnail } from "./types"

const CYCLE_INTERVAL_MS = 900

export function ClipPreviewDialog({
  open,
  onOpenChange,
  thumbnails,
  initialIndex = 0,
  title,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  thumbnails: ClipThumbnail[]
  initialIndex?: number
  title?: string
}) {
  const [index, setIndex] = React.useState(initialIndex)

  // Restart the rotation from the frame the small preview was showing
  // each time the dialog opens.
  React.useEffect(() => {
    if (open) setIndex(Math.min(initialIndex, Math.max(thumbnails.length - 1, 0)))
  }, [open, initialIndex, thumbnails.length])

  React.useEffect(() => {
    if (!open || thumbnails.length < 2) return
    const interval = window.setInterval(() => {
      setIndex((i) => (i + 1) % thumbnails.length)
    }, CYCLE_INTERVAL_MS)
    return () => window.clearInterval(interval)
  }, [open, thumbnails.length])

  const displayed = thumbnails[index]
  if (!displayed) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{title || "Clip preview"}</DialogTitle>
        </DialogHeader>

        <div className="relative aspect-video w-full overflow-hidden rounded-md bg-page">
          {displayed.url ? (
            <img src={displayed.url} alt="" className="h-full w-full object-contain" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs text-ink-muted">no image</div>
          )}
          <span className="absolute bottom-2 left-2 rounded bg-ink-display/70 px-1.5 py-0.5 text-[10px] font-medium text-page">
            {displayed.frame_timestamp_seconds.toFixed(1)}s
          </span>
        </div>

        {thumbnails.length > 1 && (
          <div className="mt-3 flex justify-center gap-1.5">
            {thumbnails.map((thumb, i) => (
              <button
                key={thumb.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Show frame at ${thumb.frame_timestamp_seconds.toFixed(1)}s`}
                className={cn(
                  "h-2 w-2 cursor-pointer rounded-full transition",
                  i === index ? "bg-accent" : "bg-hairline hover:bg-ink-muted",
                )}
              />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
